import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User,
  Lock,
  FolderOpen,
  Tag,
  Plus,
  Trash2,
  Edit3,
  Save,
  X,
  Check,
  AlertTriangle,
  Home,
  UserPlus,
  Crown,
  LogOut,
  Mail,
  Clock,
  RefreshCw,
  Bell,
  Download,
  CalendarDays,
  Copy,
  ExternalLink,
  TrendingUp,
} from 'lucide-react';
import { Card, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../api/auth';
import apiClient from '../api/client';
import { categoriesApi, tagsApi } from '../api/receipts';
import { householdsApi } from '../api/households';
import { calendarApi } from '../api/calendar';
import { budgetsApi, type Budget as BudgetItem, type BudgetCreateRequest, type BudgetUpdateRequest } from '../api/budgets';
import { toast } from '../components/ui/Toast';
import { usePushNotifications } from '../hooks/usePushNotifications';
import type { Category, Tag as TagType, Household, HouseholdInvite } from '../types';

// =============================================
// Profile Section
// =============================================
const ProfileSection: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
    }
  }, [user]);

  const mutation = useMutation({
    mutationFn: () => authApi.updateProfile({ name, email }),
    onSuccess: (updatedUser) => {
      setUser(updatedUser);
      setIsEditing(false);
      toast.success('Profil aktualisiert', 'Deine Daten wurden gespeichert.');
    },
    onError: () => {
      toast.error('Fehler', 'Profil konnte nicht aktualisiert werden.');
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <User className="w-5 h-5 text-primary-500" />
          <CardTitle>Profil</CardTitle>
        </div>
        {!isEditing && (
          <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
            <Edit3 className="w-4 h-4 mr-1" />
            Bearbeiten
          </Button>
        )}
      </CardHeader>

      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!isEditing}
          placeholder="Dein Name"
        />
        <Input
          label="E-Mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={!isEditing}
          placeholder="deine@email.de"
        />

        {isEditing && (
          <div className="flex gap-3 pt-2">
            <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>
              <Save className="w-4 h-4 mr-1" />
              Speichern
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setIsEditing(false);
                setName(user?.name || '');
                setEmail(user?.email || '');
              }}
            >
              Abbrechen
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

// =============================================
// Password Section
// =============================================
const PasswordSection: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: () =>
      authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
      toast.success('Passwort geändert', 'Dein neues Passwort ist aktiv.');
    },
    onError: () => {
      toast.error('Fehler', 'Passwort konnte nicht geändert werden. Stimmt das aktuelle Passwort?');
    },
  });

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};
    if (!currentPassword) newErrors.current = 'Aktuelles Passwort erforderlich';
    if (!newPassword) newErrors.new = 'Neues Passwort erforderlich';
    else if (newPassword.length < 8) newErrors.new = 'Mindestens 8 Zeichen';
    if (newPassword !== confirmPassword) newErrors.confirm = 'Passwörter stimmen nicht überein';

    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      mutation.mutate();
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-primary-500" />
          <CardTitle>Passwort ändern</CardTitle>
        </div>
      </CardHeader>

      <div className="space-y-4">
        <Input
          label="Aktuelles Passwort"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          error={errors.current}
          autoComplete="current-password"
        />
        <Input
          label="Neues Passwort"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          error={errors.new}
          hint="Min. 8 Zeichen, 1 Großbuchstabe, 1 Zahl, 1 Sonderzeichen"
          autoComplete="new-password"
        />
        <Input
          label="Neues Passwort bestätigen"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={errors.confirm}
          autoComplete="new-password"
        />

        <Button onClick={handleSubmit} loading={mutation.isPending}>
          <Lock className="w-4 h-4 mr-1" />
          Passwort ändern
        </Button>
      </div>
    </Card>
  );
};

// =============================================
// Categories Section
// =============================================
const CategoriesSection: React.FC = () => {
  const queryClient = useQueryClient();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState('');
  const [newColor, setNewColor] = useState('#3B82F6');

  const { data: categoryList, isLoading } = useQuery<Category[]>({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      categoriesApi.create({ name: newName, icon: newIcon || undefined, color: newColor }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setShowCreateModal(false);
      setNewName('');
      setNewIcon('');
      setNewColor('#3B82F6');
      toast.success('Kategorie erstellt');
    },
    onError: () => {
      toast.error('Fehler', 'Kategorie konnte nicht erstellt werden.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Category> }) =>
      categoriesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setEditingId(null);
      toast.success('Kategorie aktualisiert');
    },
    onError: () => {
      toast.error('Fehler', 'Kategorie konnte nicht aktualisiert werden.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoriesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Kategorie gelöscht');
    },
    onError: () => {
      toast.error('Fehler', 'Kategorie konnte nicht gelöscht werden.');
    },
  });

  const categories = categoryList || [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-primary-500" />
          <CardTitle>Kategorien</CardTitle>
        </div>
        <Button size="sm" onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-1" />
          Neu
        </Button>
      </CardHeader>

      {isLoading ? (
        <LoadingSpinner size="sm" />
      ) : categories.length > 0 ? (
        <div className="space-y-2">
          {categories.map((cat) => (
            <div
              key={cat.id}
              className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-neutral-50 dark:bg-white/5"
            >
              {editingId === cat.id ? (
                <EditCategoryRow
                  category={cat}
                  onSave={(data) => updateMutation.mutate({ id: cat.id, data })}
                  onCancel={() => setEditingId(null)}
                  loading={updateMutation.isPending}
                />
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: cat.color || '#6B7280' }}
                    />
                    <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                      {cat.name}
                    </span>
                    {cat.icon && (
                      <span className="text-xs text-neutral-400">{cat.icon}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingId(cat.id)}
                      className="p-1.5 rounded text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200
                                 hover:bg-neutral-200 dark:hover:bg-white/10 transition-colors"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Kategorie "${cat.name}" wirklich löschen?`)) {
                          deleteMutation.mutate(cat.id);
                        }
                      }}
                      className="p-1.5 rounded text-neutral-400 hover:text-error-500
                                 hover:bg-error-50 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-neutral-400 text-center py-4">
          Noch keine Kategorien vorhanden
        </p>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Neue Kategorie"
        size="sm"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="z.B. Lebensmittel"
            autoFocus
          />
          <Input
            label="Icon (optional)"
            value={newIcon}
            onChange={(e) => setNewIcon(e.target.value)}
            placeholder="z.B. mdi-cart"
          />
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
              Farbe
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                className="w-10 h-10 rounded-lg border border-neutral-300 cursor-pointer"
              />
              <span className="text-sm text-neutral-500 dark:text-neutral-400 font-mono">{newColor}</span>
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              loading={createMutation.isPending}
              disabled={!newName.trim()}
            >
              Erstellen
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
};

// =============================================
// Edit Category Row (inline)
// =============================================
const EditCategoryRow: React.FC<{
  category: Category;
  onSave: (data: Partial<Category>) => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ category, onSave, onCancel, loading }) => {
  const [name, setName] = useState(category.name);
  const [color, setColor] = useState(category.color || '#6B7280');

  return (
    <div className="flex items-center gap-2 w-full">
      <input
        type="color"
        value={color}
        onChange={(e) => setColor(e.target.value)}
        className="w-7 h-7 rounded border border-neutral-300 cursor-pointer shrink-0"
      />
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="flex-1 text-sm px-2 py-1 border border-neutral-300 dark:border-indigo-500/20 rounded
                   bg-white dark:bg-[#1e1e2e] dark:text-white
                   focus:outline-none focus:ring-1 focus:ring-primary-500"
        autoFocus
      />
      <button
        onClick={() => onSave({ name, color })}
        disabled={loading || !name.trim()}
        className="p-1.5 rounded text-success-500 hover:bg-success-50 transition-colors disabled:opacity-50"
      >
        <Check className="w-4 h-4" />
      </button>
      <button
        onClick={onCancel}
        className="p-1.5 rounded text-neutral-400 hover:bg-neutral-200 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// =============================================
// Edit Tag Row (inline)
// =============================================
const EditTagRow: React.FC<{
  tag: TagType;
  onSave: (name: string) => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ tag, onSave, onCancel, loading }) => {
  const [name, setName] = useState(tag.name);

  return (
    <div className="flex items-center gap-2 w-full">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="flex-1 text-sm px-2 py-1 border border-neutral-300 dark:border-indigo-500/20 rounded
                   bg-white dark:bg-[#1e1e2e] dark:text-white
                   focus:outline-none focus:ring-1 focus:ring-primary-500"
        autoFocus
      />
      <button
        onClick={() => onSave(name)}
        disabled={loading || !name.trim()}
        className="p-1.5 rounded text-success-500 hover:bg-success-50 transition-colors disabled:opacity-50"
      >
        <Check className="w-4 h-4" />
      </button>
      <button
        onClick={onCancel}
        className="p-1.5 rounded text-neutral-400 hover:bg-neutral-200 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// =============================================
// Tags Section
// =============================================
const TagsSection: React.FC = () => {
  const queryClient = useQueryClient();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');

  const { data: tagList, isLoading } = useQuery<TagType[]>({
    queryKey: ['tags'],
    queryFn: tagsApi.list,
  });

  const createMutation = useMutation({
    mutationFn: () => tagsApi.create(newName.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setShowCreateModal(false);
      setNewName('');
      toast.success('Tag erstellt');
    },
    onError: () => {
      toast.error('Fehler', 'Tag konnte nicht erstellt werden.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      tagsApi.update(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setEditingId(null);
      toast.success('Tag aktualisiert');
    },
    onError: () => {
      toast.error('Fehler', 'Tag konnte nicht aktualisiert werden.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => tagsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tag gelöscht');
    },
    onError: () => {
      toast.error('Fehler', 'Tag konnte nicht gelöscht werden.');
    },
  });

  const tags = tagList || [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Tag className="w-5 h-5 text-primary-500" />
          <CardTitle>Tags</CardTitle>
        </div>
        <Button size="sm" onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-1" />
          Neu
        </Button>
      </CardHeader>

      {isLoading ? (
        <LoadingSpinner size="sm" />
      ) : tags.length > 0 ? (
        <div className="space-y-2">
          {tags.map((tag) => (
            <div
              key={tag.id}
              className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-neutral-50 dark:bg-white/5"
            >
              {editingId === tag.id ? (
                <EditTagRow
                  tag={tag}
                  onSave={(name) => updateMutation.mutate({ id: tag.id, name })}
                  onCancel={() => setEditingId(null)}
                  loading={updateMutation.isPending}
                />
              ) : (
                <>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
                                   bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300">
                    <Tag className="w-3 h-3" />
                    {tag.name}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingId(tag.id)}
                      className="p-1.5 rounded text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200
                                 hover:bg-neutral-200 dark:hover:bg-white/10 transition-colors"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Tag "${tag.name}" wirklich löschen?`)) {
                          deleteMutation.mutate(tag.id);
                        }
                      }}
                      className="p-1.5 rounded text-neutral-400 hover:text-error-500
                                 hover:bg-error-50 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-neutral-400 text-center py-4">
          Noch keine Tags vorhanden
        </p>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Neuer Tag"
        size="sm"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="z.B. Arbeit, Privat, Reise"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) createMutation.mutate();
            }}
          />
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="ghost" onClick={() => { setShowCreateModal(false); setNewName(''); }}>
              Abbrechen
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              loading={createMutation.isPending}
              disabled={!newName.trim()}
            >
              Erstellen
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
};

// =============================================
// Budget Section
// =============================================
const BudgetSection: React.FC = () => {
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState<BudgetItem | null>(null);

  // Form state
  const [formCategoryId, setFormCategoryId] = useState<string>('');
  const [formAmount, setFormAmount] = useState('');
  const [formPeriod, setFormPeriod] = useState<'monthly' | 'weekly'>('monthly');
  const [formIsGlobal, setFormIsGlobal] = useState(false);

  const { data: budgets = [], isLoading } = useQuery<BudgetItem[]>({
    queryKey: ['budgets'],
    queryFn: budgetsApi.list,
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
  });

  const openCreate = () => {
    setEditingBudget(null);
    setFormCategoryId('');
    setFormAmount('');
    setFormPeriod('monthly');
    setFormIsGlobal(false);
    setShowModal(true);
  };

  const openEdit = (budget: BudgetItem) => {
    setEditingBudget(budget);
    setFormCategoryId(budget.category_id ?? '');
    setFormAmount(String(Number(budget.amount) || ''));
    setFormPeriod(budget.period);
    setFormIsGlobal(!budget.category_id);
    setShowModal(true);
  };

  const createMutation = useMutation({
    mutationFn: (data: BudgetCreateRequest) => budgetsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['budget-status'] });
      setShowModal(false);
      toast.success('Budget erstellt');
    },
    onError: () => {
      toast.error('Fehler', 'Budget konnte nicht erstellt werden.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: BudgetUpdateRequest }) =>
      budgetsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['budget-status'] });
      setShowModal(false);
      setEditingBudget(null);
      toast.success('Budget aktualisiert');
    },
    onError: () => {
      toast.error('Fehler', 'Budget konnte nicht aktualisiert werden.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => budgetsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['budget-status'] });
      toast.success('Budget gelöscht');
    },
    onError: () => {
      toast.error('Fehler', 'Budget konnte nicht gelöscht werden.');
    },
  });

  const handleSave = () => {
    const amount = parseFloat(formAmount.replace(',', '.'));
    if (!amount || amount <= 0) {
      toast.error('Ungültiger Betrag', 'Bitte gib einen gültigen Betrag ein.');
      return;
    }
    const payload: BudgetCreateRequest = {
      amount,
      period: formPeriod,
      category_id: formIsGlobal ? null : formCategoryId || null,
    };
    if (editingBudget) {
      updateMutation.mutate({ id: editingBudget.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const periodLabel = (p: 'monthly' | 'weekly') =>
    p === 'monthly' ? 'Monatlich' : 'Wöchentlich';

  const categoryName = (b: BudgetItem) =>
    b.category?.name || (b.category_id
      ? categories.find((c) => c.id === b.category_id)?.name ?? 'Unbekannte Kategorie'
      : 'Gesamt-Budget');

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-violet-500" />
          <CardTitle>Budgets</CardTitle>
        </div>
        <Button size="sm" onClick={openCreate}>
          <Plus className="w-4 h-4 mr-1" />
          Neu
        </Button>
      </CardHeader>

      {isLoading ? (
        <LoadingSpinner size="sm" />
      ) : budgets.length > 0 ? (
        <div className="space-y-2">
          {budgets.map((budget) => (
            <div
              key={budget.id}
              className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-neutral-50 dark:bg-white/5"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 truncate">
                  {categoryName(budget)}
                </p>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                  {Number(budget.amount).toLocaleString('de-DE', {
                    style: 'currency',
                    currency: 'EUR',
                  })}{' '}
                  · {periodLabel(budget.period)}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => openEdit(budget)}
                  className="p-1.5 rounded text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200
                             hover:bg-neutral-200 dark:hover:bg-white/10 transition-colors"
                  aria-label="Budget bearbeiten"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`Budget "${categoryName(budget)}" wirklich löschen?`)) {
                      deleteMutation.mutate(budget.id);
                    }
                  }}
                  className="p-1.5 rounded text-neutral-400 hover:text-error-500
                             hover:bg-error-50 transition-colors"
                  aria-label="Budget löschen"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-neutral-400 text-center py-4">
          Noch keine Budgets festgelegt
        </p>
      )}

      {/* Create / Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingBudget(null); }}
        title={editingBudget ? 'Budget bearbeiten' : 'Neues Budget'}
        size="sm"
      >
        <div className="space-y-4">
          {/* Gesamt-Budget Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Gesamt-Budget (ohne Kategorie)
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={formIsGlobal}
              onClick={() => setFormIsGlobal((v) => !v)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full
                transition-colors duration-200 focus:outline-none focus-visible:ring-2
                focus-visible:ring-primary-500
                ${formIsGlobal ? 'bg-primary-500' : 'bg-neutral-300 dark:bg-neutral-600'}`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow
                  transition-transform duration-200
                  ${formIsGlobal ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          </div>

          {/* Kategorie-Dropdown (nur wenn kein Gesamt-Budget) */}
          {!formIsGlobal && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                Kategorie
              </label>
              <select
                value={formCategoryId}
                onChange={(e) => setFormCategoryId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-indigo-500/20
                           bg-white dark:bg-[#1e1e2e] text-neutral-800 dark:text-white text-sm
                           focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Kategorie wählen…</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Betrag */}
          <Input
            label="Limit (EUR)"
            type="number"
            min="0.01"
            step="0.01"
            value={formAmount}
            onChange={(e) => setFormAmount(e.target.value)}
            placeholder="z.B. 250"
          />

          {/* Periode */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
              Periode
            </label>
            <div className="flex gap-2">
              {(['monthly', 'weekly'] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setFormPeriod(p)}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-all
                    ${formPeriod === p
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-white dark:bg-[#1e1e2e] text-neutral-600 dark:text-neutral-300 border-neutral-300 dark:border-indigo-500/20 hover:border-primary-400'
                    }`}
                >
                  {periodLabel(p)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <Button variant="ghost" onClick={() => { setShowModal(false); setEditingBudget(null); }}>
              Abbrechen
            </Button>
            <Button
              onClick={handleSave}
              loading={isSaving}
              disabled={!formAmount || (!formIsGlobal && !formCategoryId)}
            >
              {editingBudget ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
};

// =============================================
// Notifications Section
// =============================================
const NotificationsSection: React.FC = () => {
  const { isSupported, isSubscribed, permission, isLoading, subscribe, unsubscribe, sendTest } =
    usePushNotifications();

  const handleToggle = async () => {
    try {
      if (isSubscribed) {
        await unsubscribe();
        toast.success('Benachrichtigungen deaktiviert', 'Du erhältst keine Push-Benachrichtigungen mehr.');
      } else {
        await subscribe();
        if (Notification.permission === 'granted') {
          toast.success('Benachrichtigungen aktiviert', 'Du wirst jetzt über neue Ereignisse informiert.');
        } else if (Notification.permission === 'denied') {
          toast.error('Berechtigung verweigert', 'Bitte erlaube Benachrichtigungen in deinen Browser-Einstellungen.');
        }
      }
    } catch {
      toast.error('Fehler', 'Benachrichtigungen konnten nicht geändert werden.');
    }
  };

  const handleTest = async () => {
    try {
      await sendTest();
      toast.success('Test gesendet', 'Du solltest gleich eine Benachrichtigung erhalten.');
    } catch {
      toast.error('Fehler', 'Test-Benachrichtigung konnte nicht gesendet werden.');
    }
  };

  const getStatusLabel = () => {
    if (!isSupported) return { text: 'Nicht unterstützt', color: 'text-neutral-400' };
    if (permission === 'denied') return { text: 'Vom Browser blockiert', color: 'text-red-500' };
    if (isSubscribed) return { text: 'Aktiviert', color: 'text-green-500 dark:text-green-400' };
    return { text: 'Deaktiviert', color: 'text-neutral-400' };
  };

  const status = getStatusLabel();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary-500" />
          <CardTitle>Benachrichtigungen</CardTitle>
        </div>
      </CardHeader>

      <div className="space-y-4">
        {/* Status & Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
              Push-Benachrichtigungen
            </p>
            <p className={`text-xs mt-0.5 ${status.color}`}>{status.text}</p>
          </div>

          {isSupported && permission !== 'denied' && (
            <button
              type="button"
              role="switch"
              aria-checked={isSubscribed}
              disabled={isLoading}
              onClick={handleToggle}
              className={`
                relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full
                transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2
                focus-visible:ring-primary-500 focus-visible:ring-offset-2
                disabled:cursor-not-allowed disabled:opacity-50
                ${isSubscribed ? 'bg-primary-500' : 'bg-neutral-300 dark:bg-neutral-600'}
              `}
            >
              <span
                className={`
                  pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow
                  transition-transform duration-200 ease-in-out
                  ${isSubscribed ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          )}
        </div>

        {/* Hinweis wenn Permission blockiert */}
        {isSupported && permission === 'denied' && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Benachrichtigungen sind in deinen Browser-Einstellungen blockiert. Bitte entsperre sie dort,
            um Push-Benachrichtigungen zu aktivieren.
          </p>
        )}

        {/* Nicht unterstützt */}
        {!isSupported && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Dein Browser unterstützt keine Push-Benachrichtigungen. Nutze einen modernen Browser wie
            Chrome, Firefox oder Safari.
          </p>
        )}

        {/* Test-Button */}
        {isSubscribed && (
          <Button variant="ghost" size="sm" onClick={handleTest} loading={isLoading}>
            <Bell className="w-4 h-4 mr-1" />
            Test-Benachrichtigung senden
          </Button>
        )}
      </div>
    </Card>
  );
};

// =============================================
// Calendar Subscription Section
// =============================================
const CalendarSubscriptionSection: React.FC = () => {
  const [feedUrl, setFeedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const generateMutation = useMutation({
    mutationFn: calendarApi.generateToken,
    onSuccess: (data) => {
      setFeedUrl(data.feed_url);
      toast.success('Feed-URL erstellt', 'Deine Kalender-URL wurde generiert.');
    },
    onError: () => {
      toast.error('Fehler', 'Feed-URL konnte nicht erstellt werden.');
    },
  });

  const handleCopy = () => {
    if (!feedUrl) return;
    navigator.clipboard.writeText(feedUrl);
    setCopied(true);
    toast.success('Kopiert!', 'URL in Zwischenablage kopiert.');
    setTimeout(() => setCopied(false), 2000);
  };

  const webcalUrl = feedUrl ? feedUrl.replace(/^https?:\/\//, 'webcal://') : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-primary-500" />
          <CardTitle>Kalender-Abo</CardTitle>
        </div>
      </CardHeader>

      <div className="space-y-4">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Abonniere deine Aufgaben und Fixkosten in deinem Kalender (iOS, Android, Google)
        </p>

        {!feedUrl ? (
          <Button
            onClick={() => generateMutation.mutate()}
            loading={generateMutation.isPending}
          >
            <CalendarDays className="w-4 h-4 mr-1.5" />
            Feed-URL generieren
          </Button>
        ) : (
          <div className="space-y-3">
            {/* URL-Feld mit Kopieren-Button */}
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={feedUrl}
                onClick={(e) => (e.target as HTMLInputElement).select()}
                className="flex-1 text-xs px-3 py-2 rounded-lg bg-green-50 dark:bg-green-500/10
                           border border-green-200 dark:border-green-500/30
                           text-green-800 dark:text-green-300 truncate cursor-text"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleCopy}
                className="shrink-0"
              >
                {copied ? (
                  <Check className="w-4 h-4 mr-1 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4 mr-1" />
                )}
                {copied ? 'Kopiert' : 'Kopieren'}
              </Button>
            </div>

            {/* In Kalender öffnen */}
            <a
              href={webcalUrl!}
              className="inline-flex items-center gap-1.5 text-sm font-medium
                         text-green-600 dark:text-green-400 hover:text-green-700
                         dark:hover:text-green-300 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              In Kalender öffnen (automatisches Abo)
            </a>

            {/* Hinweis */}
            <p className="text-xs text-neutral-400 dark:text-neutral-500">
              Füge diese URL als Kalender-Abo hinzu. Der Kalender aktualisiert sich automatisch.
            </p>

            {/* Anleitungen */}
            <div className="space-y-1.5 pt-1 border-t border-neutral-100 dark:border-neutral-800">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Anleitung:</p>
              <p className="text-xs text-neutral-400 dark:text-neutral-500">
                <span className="font-medium text-neutral-600 dark:text-neutral-300">iOS:</span>{' '}
                Einstellungen → Kalender → Accounts → Abo hinzufügen
              </p>
              <p className="text-xs text-neutral-400 dark:text-neutral-500">
                <span className="font-medium text-neutral-600 dark:text-neutral-300">Android:</span>{' '}
                Google Kalender → Einstellungen → Abo hinzufügen
              </p>
            </div>

            {/* Neu generieren */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => generateMutation.mutate()}
              loading={generateMutation.isPending}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Neue URL generieren
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

// =============================================
// Household Section
// =============================================
const HouseholdSection: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [createName, setCreateName] = useState('');
  const [editName, setEditName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [showInviteInput, setShowInviteInput] = useState(false);

  const { data: households, isLoading } = useQuery<Household[]>({
    queryKey: ['households'],
    queryFn: householdsApi.list,
  });

  const household = households && households.length > 0 ? households[0] : null;
  const currentMember = household?.members.find((m) => m.user_id === user?.id);
  const isAdmin = currentMember?.role === 'admin';

  const { data: invites } = useQuery<HouseholdInvite[]>({
    queryKey: ['household-invites', household?.id],
    queryFn: () => householdsApi.getInvites(household!.id),
    enabled: !!household && isAdmin,
  });

  const createMutation = useMutation({
    mutationFn: () => householdsApi.create(createName.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['households'] });
      setCreateName('');
      toast.success('Haushalt erstellt', 'Dein Haushalt wurde erfolgreich angelegt.');
    },
    onError: () => {
      toast.error('Fehler', 'Haushalt konnte nicht erstellt werden.');
    },
  });

  const updateNameMutation = useMutation({
    mutationFn: () => householdsApi.update(household!.id, editName.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['households'] });
      setIsEditingName(false);
      toast.success('Haushalt aktualisiert');
    },
    onError: () => {
      toast.error('Fehler', 'Haushalt-Name konnte nicht geändert werden.');
    },
  });

  const [lastInviteLink, setLastInviteLink] = useState<string | null>(null);

  const inviteMutation = useMutation({
    mutationFn: () => householdsApi.invite(household!.id, inviteEmail.trim()),
    onSuccess: (invite) => {
      queryClient.invalidateQueries({ queryKey: ['household-invites', household?.id] });
      const link = `${window.location.origin}/invite/${invite.token}`;
      setLastInviteLink(link);
      setInviteEmail('');
      setShowInviteInput(false);
      toast.success('Einladung erstellt', 'Teile den Link per WhatsApp oder kopiere ihn.');
    },
    onError: () => {
      toast.error('Fehler', 'Einladung konnte nicht gesendet werden.');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => householdsApi.removeMember(household!.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['households'] });
      toast.success('Mitglied entfernt');
    },
    onError: () => {
      toast.error('Fehler', 'Mitglied konnte nicht entfernt werden.');
    },
  });

  const leaveMutation = useMutation({
    mutationFn: () => householdsApi.leave(household!.id, user!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['households'] });
      toast.success('Haushalt verlassen');
    },
    onError: () => {
      toast.error('Fehler', 'Haushalt konnte nicht verlassen werden.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => householdsApi.delete(household!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['households'] });
      toast.success('Haushalt gelöscht');
    },
    onError: () => {
      toast.error('Fehler', 'Haushalt konnte nicht gelöscht werden.');
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => householdsApi.sync(household!.id),
    onSuccess: (data) => {
      const s = data.synced;
      const parts: string[] = [];
      if (s.receipts > 0) parts.push(`${s.receipts} Belege`);
      if (s.budgets > 0) parts.push(`${s.budgets} Budgets`);
      if (s.recurring_expenses > 0) parts.push(`${s.recurring_expenses} Fixkosten`);
      if (s.shopping_lists > 0) parts.push(`${s.shopping_lists} Einkaufslisten`);
      if (s.todo_lists > 0) parts.push(`${s.todo_lists} Aufgabenlisten`);
      if (parts.length > 0) {
        toast.success('Synchronisiert', parts.join(', '));
      } else {
        toast.success('Alles synchron', 'Keine neuen Daten zum Synchronisieren.');
      }
      queryClient.invalidateQueries();
    },
    onError: () => {
      toast.error('Fehler', 'Synchronisierung fehlgeschlagen.');
    },
  });

  const pendingInvites = (invites || []).filter((inv) => inv.status === 'pending');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Home className="w-5 h-5 text-primary-500" />
          <CardTitle>Haushalt</CardTitle>
        </div>
      </CardHeader>

      {isLoading ? (
        <LoadingSpinner size="sm" />
      ) : !household ? (
        /* ---- Kein Haushalt vorhanden ---- */
        <div className="space-y-4">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Du bist noch keinem Haushalt zugeordnet. Erstelle einen neuen Haushalt, um
            Kassenzettel mit anderen Personen zu teilen.
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="Name des Haushalts, z.B. Familie Müller"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && createName.trim()) createMutation.mutate();
              }}
            />
            <Button
              onClick={() => createMutation.mutate()}
              loading={createMutation.isPending}
              disabled={!createName.trim()}
            >
              <Plus className="w-4 h-4 mr-1" />
              Erstellen
            </Button>
          </div>
        </div>
      ) : (
        /* ---- Haushalt vorhanden ---- */
        <div className="space-y-5">
          {/* Haushalt-Name */}
          <div>
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 text-sm px-3 py-2 border border-neutral-300 dark:border-indigo-500/20 rounded-lg
                             bg-white dark:bg-[#1e1e2e] dark:text-white
                             focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                  autoFocus
                />
                <button
                  onClick={() => updateNameMutation.mutate()}
                  disabled={updateNameMutation.isPending || !editName.trim()}
                  className="p-2 rounded-lg text-success-500 hover:bg-success-50 transition-colors disabled:opacity-50"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsEditingName(false)}
                  className="p-2 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wide font-medium mb-0.5">
                    Haushalt
                  </p>
                  <p className="text-base font-semibold text-neutral-800 dark:text-neutral-100">
                    {household.name}
                  </p>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => {
                      setEditName(household.name);
                      setIsEditingName(true);
                    }}
                    className="p-1.5 rounded text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200
                               hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Mitglieder-Liste */}
          <div>
            <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wide font-medium mb-2">
              Mitglieder ({household.members.length})
            </p>
            <div className="space-y-2">
              {household.members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-neutral-50 dark:bg-white/5"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-500/20 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-primary-600 dark:text-primary-400">
                        {member.user_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200 truncate">
                        {member.user_name}
                        {member.user_id === user?.id && (
                          <span className="ml-1.5 text-xs text-neutral-400">(du)</span>
                        )}
                      </p>
                      <p className="text-xs text-neutral-400 truncate">{member.user_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                        ${member.role === 'admin'
                          ? 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400'
                          : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
                        }`}
                    >
                      {member.role === 'admin' && <Crown className="w-3 h-3" />}
                      {member.role === 'admin' ? 'Admin' : 'Mitglied'}
                    </span>
                    {isAdmin && member.user_id !== user?.id && (
                      <button
                        onClick={() => {
                          if (window.confirm(`${member.user_name} wirklich aus dem Haushalt entfernen?`)) {
                            removeMemberMutation.mutate(member.user_id);
                          }
                        }}
                        className="p-1.5 rounded text-neutral-400 hover:text-error-500
                                   hover:bg-error-50 transition-colors"
                        title="Mitglied entfernen"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Daten synchronisieren */}
          <div className="p-3 rounded-xl bg-primary-50/50 dark:bg-primary-500/5 border border-primary-100 dark:border-primary-500/10">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                  Daten synchronisieren
                </p>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                  Alle persönlichen Daten mit dem Haushalt teilen
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => syncMutation.mutate()}
                loading={syncMutation.isPending}
              >
                <RefreshCw className={`w-4 h-4 mr-1.5 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                Sync
              </Button>
            </div>
          </div>

          {/* Mitglied einladen */}
          {isAdmin && (
            <div>
              {showInviteInput ? (
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <input
                      type="email"
                      placeholder="E-Mail-Adresse eingeben..."
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && inviteEmail.trim()) inviteMutation.mutate();
                        if (e.key === 'Escape') {
                          setShowInviteInput(false);
                          setInviteEmail('');
                        }
                      }}
                      autoFocus
                      className="w-full pl-10 pr-4 py-2 text-sm border border-neutral-300 dark:border-indigo-500/20 rounded-lg
                                 bg-white dark:bg-[#1e1e2e] dark:text-white placeholder:text-neutral-400
                                 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => inviteMutation.mutate()}
                    loading={inviteMutation.isPending}
                    disabled={!inviteEmail.trim()}
                  >
                    Einladen
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowInviteInput(false);
                      setInviteEmail('');
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowInviteInput(true)}
                >
                  <UserPlus className="w-4 h-4 mr-1.5" />
                  Mitglied einladen
                </Button>
              )}
            </div>
          )}

          {/* Einladungslink teilen */}
          {lastInviteLink && (
            <div className="p-3 rounded-lg bg-success-50 dark:bg-success-500/10 border border-success-200 dark:border-success-500/20 space-y-3">
              <p className="text-sm font-medium text-success-700 dark:text-success-400">
                Einladungslink erstellt!
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={lastInviteLink}
                  className="flex-1 text-xs px-2 py-1.5 rounded bg-white dark:bg-[#1a1a2e] border border-neutral-300 dark:border-indigo-500/20 text-neutral-600 dark:text-neutral-300 truncate"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(lastInviteLink);
                    toast.success('Kopiert!', 'Link in Zwischenablage kopiert.');
                  }}
                >
                  Kopieren
                </Button>
              </div>
              <div className="flex gap-2">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Hey! Tritt unserem Haushalt in PaarKurs bei: ${lastInviteLink}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                             bg-[#25D366] text-white hover:bg-[#1da851] transition-colors"
                >
                  Per WhatsApp teilen
                </a>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setLastInviteLink(null)}
                >
                  Schließen
                </Button>
              </div>
            </div>
          )}

          {/* Offene Einladungen */}
          {isAdmin && pendingInvites.length > 0 && (
            <div>
              <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wide font-medium mb-2">
                Offene Einladungen ({pendingInvites.length})
              </p>
              <div className="space-y-2">
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center gap-3 py-2 px-3 rounded-lg bg-warning-50 dark:bg-warning-500/10
                               border border-warning-200 dark:border-warning-500/20"
                  >
                    <Clock className="w-4 h-4 text-warning-500 shrink-0" />
                    <span className="text-sm text-warning-700 dark:text-warning-400 truncate flex-1">
                      {invite.email}
                    </span>
                    <span className="text-xs text-warning-500 shrink-0">Ausstehend</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Haushalt verlassen / löschen */}
          <div className="pt-2 border-t border-neutral-200 dark:border-indigo-500/10">
            {isAdmin ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (window.confirm('Haushalt wirklich löschen? Alle Mitglieder werden entfernt und Haushalt-Daten gehen verloren.')) {
                    deleteMutation.mutate();
                  }
                }}
                loading={deleteMutation.isPending}
                className="!border-error-300 !text-error-600 hover:!bg-error-50"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                Haushalt löschen
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (window.confirm('Haushalt wirklich verlassen?')) {
                    leaveMutation.mutate();
                  }
                }}
                loading={leaveMutation.isPending}
                className="!border-error-300 !text-error-600 hover:!bg-error-50"
              >
                <LogOut className="w-4 h-4 mr-1.5" />
                Haushalt verlassen
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

// =============================================
// Data Backup Section
// =============================================
const DataBackupSection: React.FC = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await apiClient.get('/auth/admin/export-data', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paarkurs-backup-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Backup heruntergeladen', 'Die JSON-Datei wurde gespeichert.');
    } catch {
      toast.error('Fehler', 'Backup konnte nicht heruntergeladen werden.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Download className="w-5 h-5 text-primary-500" />
          <CardTitle>Datensicherung</CardTitle>
        </div>
      </CardHeader>

      <div className="space-y-4">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Exportiere alle deine Daten als JSON-Backup.
        </p>
        <Button
          variant="outline"
          onClick={handleDownload}
          loading={isDownloading}
        >
          <Download className="w-4 h-4 mr-1.5" />
          Backup herunterladen
        </Button>
      </div>
    </Card>
  );
};

// =============================================
// Danger Zone — Konto loeschen
// =============================================
const DangerZoneSection: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  const deleteMutation = useMutation({
    mutationFn: () => authApi.deleteAccount(),
    onSuccess: () => {
      toast.success('Konto geloescht', 'Dein Konto wurde unwiderruflich geloescht.');
      logout();
      navigate('/login');
    },
    onError: () => {
      toast.error('Fehler', 'Konto konnte nicht geloescht werden.');
    },
  });

  const handleDelete = () => {
    if (
      window.confirm(
        'Bist du sicher, dass du dein Konto unwiderruflich loeschen moechtest? Alle Daten gehen verloren.'
      )
    ) {
      deleteMutation.mutate();
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-error-500" />
          <CardTitle>Gefahrenzone</CardTitle>
        </div>
      </CardHeader>

      <div className="space-y-3">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Wenn du dein Konto loeschst, werden alle deine Daten unwiderruflich entfernt.
          Dieser Vorgang kann nicht rueckgaengig gemacht werden.
        </p>
        <Button
          variant="outline"
          onClick={handleDelete}
          loading={deleteMutation.isPending}
          className="!border-error-300 !text-error-600 hover:!bg-error-50"
        >
          <Trash2 className="w-4 h-4 mr-1" />
          Konto loeschen
        </Button>
      </div>
    </Card>
  );
};

// =============================================
// Settings Page
// =============================================
export const SettingsPage: React.FC = () => {
  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-neutral-800 dark:text-white">Einstellungen</h1>
        <p className="text-neutral-500 dark:text-neutral-400 mt-1">Verwalte dein Konto und Kategorien</p>
      </div>

      <ProfileSection />
      <CategoriesSection />
      <TagsSection />
      <BudgetSection />
      <NotificationsSection />
      <CalendarSubscriptionSection />
      <HouseholdSection />
      <DataBackupSection />
      <PasswordSection />
      <DangerZoneSection />
    </div>
  );
};
